import { useState } from 'react';
import { AnswerFrame, AnswerMedallion, BlossomMark, CeremonialPanelFrame, OrnateControlFrame } from '../components/CeremonialFrames';
import { ExperienceDisclaimer } from '../components/ExperienceDisclaimer';
import { questions } from '../lib/algorithm';
import type { Answer } from '../types';

type Props = {
  index: number;
  answers: (Answer | null)[];
  onSelect: (answer: Answer) => void;
  onMove: (index: number) => void;
  onComplete: () => void;
};

export function QuizPage({ index, answers, onSelect, onMove, onComplete }: Props) {
  const [pending, setPending] = useState(false);
  const question = questions[index];
  const selected = answers[index];
  const isLast = index === questions.length - 1;
  const canComplete = answers.every(Boolean);

  function choose(answer: Answer) {
    if (pending) return;
    if ('vibrate' in navigator) navigator.vibrate(12);
    onSelect(answer);
    if (!isLast) {
      setPending(true);
      window.setTimeout(() => { onMove(index + 1); setPending(false); }, 240);
    }
  }

  const progress = ((index + 1) / questions.length) * 100;

  return (
    <main className="quiz-page">
      <section className="quiz-shell" aria-labelledby="question-title">
        <header className="quiz-progress-header">
          <div className="question-counter">
            <i className="counter-branch branch-left" aria-hidden="true">✿</i>
            <strong>{String(index + 1).padStart(2, '0')}</strong><span>/ {questions.length}</span>
            <i className="counter-branch branch-right" aria-hidden="true">✿</i>
          </div>
          <div className="progress-track" role="progressbar" aria-valuemin={1} aria-valuemax={questions.length} aria-valuenow={index + 1}>
            <span style={{ width: `${progress}%` }} />
            <BlossomMark className="progress-blossom" style={{ left: `clamp(12px, ${progress}%, calc(100% - 12px))` }} />
          </div>
        </header>

        <div className={`question-card ${pending ? 'answer-pending' : ''}`} key={question.id}>
          <CeremonialPanelFrame />
          <h1 id="question-title">{question.text}</h1>
          <div className="answer-list">
            {question.options.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`answer-option ${selected === option.id ? 'selected' : ''}`}
                aria-pressed={selected === option.id}
                onClick={() => choose(option.id)}
              >
                <AnswerFrame />
                <AnswerMedallion>{option.id}</AnswerMedallion>
                <em>{option.text}</em>
              </button>
            ))}
          </div>
          <p className="auto-note">✿ 点击选项后自动记录</p>
        </div>

        <nav className="quiz-nav" aria-label="题目导航">
          <button type="button" onClick={() => onMove(index - 1)} disabled={index === 0}>
            <OrnateControlFrame tone="rose" /><span>‹&nbsp; 上一题</span>
          </button>
          {isLast ? (
            <button type="button" onClick={onComplete} disabled={!canComplete}>
              <OrnateControlFrame tone="rose" /><span>查看结果&nbsp; ›</span>
            </button>
          ) : (
            <button type="button" onClick={() => onMove(index + 1)} disabled={!selected}>
              <OrnateControlFrame tone="rose" /><span>下一题&nbsp; ›</span>
            </button>
          )}
        </nav>
        <ExperienceDisclaimer className="quiz-disclaimer" />
      </section>
    </main>
  );
}
